import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  forgottonPassword,
  verifyEmailByOtpGiftCard,
} from "../../../reducers/authentication.slice";
import { getUsers } from "../../../utils/getUser";
import { createRedemption } from "../../../reducers/giftcard.slice";
import OtpInput from "react-otp-input";
import { AuthContext } from "../../../context/authContext";
import { companyConfig } from "../myGiftCardList";
import { ArrowLeft, ChevronDown, Info, Minus, Plus } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Select, SelectOption } from "../../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";

const GiftCard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [amount, setAmount] = useState("");
  const [quantity, setQuantity] = useState(1);
  const { walletSummary, isWalletRefresh, setIsWalletRefresh, showToast } =
    useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [codeError, setCodeError] = useState(false);
  const [codeErrorMessage, setCodeErrorMessage] = useState("");
  const [cardetails, setCardDetails] = useState({});
  const [formErrors, setFormErrors] = useState({
    amount: { error: false, message: "" },
    quantity: { error: false, message: "" },
  });
  const [amountDenomination, setAmountDenomination] = useState([]);
  const [redempData, setRedemData] = useState({ actualAmount: "", discountedAmount: "" });
  const [showLoader, setShowLoader] = useState(false);

  const user = getUsers();

  useEffect(() => {
    if (location?.state?.card) {
      const cardData = location.state.card;
      const denominations = cardData[1]?.gift_cards?.map((card) => card.amount) || [];
      setAmountDenomination(denominations);
      const selectedAmount = denominations.length > 0 ? denominations[0] : 0;
      const selectedCard = cardData[1]?.gift_cards?.find((card) => card.amount === selectedAmount);
      if (selectedCard) {
        const boost = selectedCard.boost || 10;
        const boostAmount = ((selectedAmount * boost) / 100).toFixed(4);
        const discountedAmount = (selectedAmount - boostAmount).toFixed(2);
        setAmount(selectedAmount);
        setRedemData({ actualAmount: selectedAmount, discountedAmount });
        setCardDetails({ ...selectedCard, discountedAmount, newTotalAmount: selectedAmount, retailer: cardData[0] });
        if (parseFloat(discountedAmount) > walletSummary?.current_balance) {
          setFieldError("quantity", true, `Insufficient balance. Available: $${walletSummary?.current_balance.toFixed(2)}`);
        } else {
          setFieldError("quantity", false);
        }
      }
    }
  }, [location?.state?.card, walletSummary?.current_balance]);

  const setFieldError = (field, hasError, message = "") => {
    setFormErrors((prev) => ({ ...prev, [field]: { error: hasError, message } }));
  };

  const handleClose = () => {
    setOpen(false);
    setVerificationCode("");
    setCodeError(false);
  };

  const handleSendOtp = () => {
    if (redempData?.discountedAmount > walletSummary?.current_balance) {
      showToast("Insufficient balance", "error");
      return;
    }
    let hasError = false;
    if (!amount) { setFieldError("amount", true, "Please select an amount"); hasError = true; }
    const maxAllowed = cardetails?.quantity || 1;
    if (quantity > maxAllowed) { setFieldError("quantity", true, `Maximum available quantity is ${maxAllowed}`); hasError = true; }
    if (hasError) return;
    setCodeErrorMessage("");
    setShowLoader(true);
    dispatch(forgottonPassword({ email: user?.email, email_type: "WITHDRAWAL_OTP" })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      setOpen(true);
      showToast("Verification code has been successfully sent", "success");
      setShowLoader(false);
    });
  };

  const handleVerifyCode = (event) => {
    setShowLoader(true);
    event.preventDefault();
    if (verificationCode.length === 6) {
      const singleCardActualAmount = amount;
      const selectedCard = location.state.card[1]?.gift_cards?.find((card) => card.amount === amount);
      const boost = selectedCard?.boost || 10;
      const boostAmount = ((singleCardActualAmount * boost) / 100).toFixed(4);
      const singleCardDiscountedAmount = (singleCardActualAmount - boostAmount).toFixed(2);
      const payload = {
        gift_card_id: cardetails?.id,
        actual_amount: parseFloat(singleCardActualAmount),
        discounted_amount: parseFloat(singleCardDiscountedAmount),
        quantity: 1,
        status: "PENDING_APPROVAL",
      };
      let request_list = [];
      for (let i = 0; i <= quantity - 1; i++) request_list.push(payload);
      dispatch(verifyEmailByOtpGiftCard({ id: user?.id, otp: verificationCode })).then((res) => {
        if (res?.error && Object.keys(res?.error)?.length > 0) {
          setCodeError(true);
          setShowLoader(false);
          setCodeErrorMessage(res?.error?.message);
          return;
        }
        dispatch(createRedemption({ request_list })).then((res) => {
          if (res?.error && Object.keys(res?.error)?.length > 0) {
            showToast(res?.error?.message, "error");
            handleClose();
            setShowLoader(false);
            return;
          }
          setIsWalletRefresh(!isWalletRefresh);
          handleClose();
          setShowLoader(false);
          showToast("Gift card purchased successfully", "success");
          navigate("/dashboard/wallet/gift-cards");
        });
      });
    } else {
      setCodeError(true);
    }
  };

  const handleIncrease = () => {
    const currentQuantity = quantity === "" ? 0 : Number(quantity);
    handleQuantityChange(currentQuantity + 1);
  };

  const handleDecrease = () => {
    const currentQuantity = quantity === "" ? 0 : Number(quantity);
    if (currentQuantity > 1) handleQuantityChange(currentQuantity - 1);
  };

  const handleQuantityChange = (newValue) => {
    newValue = Number(newValue);
    const selectedCard = location.state.card[1]?.gift_cards?.find((card) => card.amount === amount);
    const maxAllowed = selectedCard?.quantity || 1;
    if (newValue === 0) { setQuantity(newValue); setFieldError("quantity", true, "Quantity must be at least 1"); return; }
    if (newValue < 0) { setQuantity(newValue); setFieldError("quantity", true, "Quantity cannot be negative"); return; }
    if (newValue > maxAllowed) { setQuantity(newValue); setFieldError("quantity", true, `Maximum available quantity is ${maxAllowed}`); return; }
    setQuantity(newValue);
    if (selectedCard) {
      const boost = selectedCard.boost || 10;
      const newTotalAmount = amount * newValue;
      const boostAmount = ((newTotalAmount * boost) / 100).toFixed(4);
      const discountedAmount = (newTotalAmount - boostAmount).toFixed(2);
      if (parseFloat(discountedAmount) > walletSummary?.current_balance) {
        setFieldError("quantity", true, `Insufficient balance. Available: $${walletSummary?.current_balance.toFixed(2)}`);
      } else {
        setFieldError("quantity", false);
      }
      setRedemData({ actualAmount: newTotalAmount, discountedAmount });
      setCardDetails((prev) => ({ ...prev, newTotalAmount, discountedAmount }));
    }
  };

  const findBgColor = (cardType) => {
    const { logo, bg } = companyConfig[cardType] || { logo: "/images/amazon.png", bg: "#000000" };
    return { bgColor: bg, bgLogo: logo };
  };

  const handleAmountChange = (newAmount) => {
    setAmount(newAmount);
    setQuantity(1);
    const selectedCard = location.state.card[1]?.gift_cards?.find((card) => card.amount === newAmount);
    if (selectedCard) {
      const boost = selectedCard.boost || 10;
      const boostAmount = ((newAmount * 1 * boost) / 100).toFixed(4);
      const discountedAmount = (newAmount * 1 - boostAmount).toFixed(2);
      setRedemData({ actualAmount: newAmount * 1, discountedAmount });
      setCardDetails({ ...selectedCard, discountedAmount, retailer: location.state.card[0], newTotalAmount: newAmount * 1 });
      if (parseFloat(discountedAmount) > walletSummary?.current_balance) {
        setFieldError("quantity", true, `Insufficient balance. Available: $${walletSummary?.current_balance.toFixed(2)}`);
      } else {
        setFieldError("quantity", false);
      }
    }
  };

  const cardTypeDisplay = () => {
    if (!cardetails?.gift_card_type) return "";
    if (cardetails.gift_card_type.length === 1) return cardetails.gift_card_type[0];
    return cardetails.gift_card_type.join(" & ").replace("_", " ");
  };

  const retailerTitle = () =>
    cardetails?.retailer
      ?.split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

  return (
    <>
      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
        input[type=number]:focus { border: 2px solid #00ADB5 !important; box-shadow: 0 0 0 2px rgba(0,173,181,0.2); }
      `}</style>

      <div style={{ marginTop: 24, padding: 16, borderRadius: 8, border: "1px solid #3C3C3C" }}>
        <div
          style={{ display: "flex", alignItems: "center", gap: 8, color: "rgba(255,255,255,0.52)", cursor: "pointer", marginBottom: 16 }}
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={18} />
          <span style={{ fontFamily: "inter", fontSize: 14 }}>Back</span>
        </div>

        {/* Card Logo Banner */}
        <div style={{
          background: "linear-gradient(rgb(0 0 0 / 27%) 20%, rgb(0, 0, 0) 100%)",
          backgroundColor: findBgColor(cardetails?.retailer)?.bgColor,
          borderRadius: 4,
          height: 230,
          position: "relative",
        }}>
          <div style={{
            height: 25,
            padding: "4px 8px",
            boxShadow: "3px 1px 20px 1px rgba(0,0,0,0.27)",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundImage: findBgColor(cardetails?.retailer)?.bgColor
              ? `linear-gradient(to right, rgba(255,255,255,0), #ff00007d 30%, #ff0000b8 50%, rgba(255,255,255,0))`
              : `linear-gradient(to right, rgba(255,255,255,0.2), rgba(255,255,255,0.4), rgba(255,255,255,0.2))`,
          }}>
            <div style={{ fontWeight: 600, fontSize: "0.7rem", letterSpacing: 1, textTransform: "uppercase" }}>
              {cardetails.boost}% BOOST
            </div>
          </div>
          <div style={{ height: 170, padding: "48px 59px", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: 5 }}>
            <div style={{ marginBottom: 5 }}>
              <img src={findBgColor(cardetails?.retailer)?.bgLogo} alt={cardetails.retailer} style={{ width: 155, height: 60, objectFit: "contain" }} />
            </div>
            <span style={{ fontWeight: 400, fontSize: "0.9rem", lineHeight: "100%", letterSpacing: 2, textTransform: "uppercase" }}>
              {cardTypeDisplay()}
            </span>
          </div>
        </div>

        {/* Action Area */}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div style={{ width: "100%", maxWidth: 800, margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: 16 }}>
            <h2 style={{ fontWeight: 600, letterSpacing: 1.5, fontSize: "1.4rem", textAlign: "center", marginBottom: 16 }}>
              {retailerTitle()} Gift Card
            </h2>

            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 5 }}>
              {/* Amount + Qty row */}
              <div style={{ display: "flex", gap: 16, flexDirection: "row", flexWrap: "wrap" }}>
                {/* Amount */}
                <div style={{ flex: "3 1 120px" }}>
                  <label style={{ display: "block", color: "#fff", marginBottom: 6, fontFamily: "Inter", letterSpacing: "0.8px", marginTop: 10, fontSize: 14 }}>
                    Amount
                  </label>
                  <Select
                    value={amount}
                    onChange={(e) => handleAmountChange(Number(e.target.value))}
                  >
                    {amountDenomination.map((value) => (
                      <SelectOption key={value} value={value}>${value}</SelectOption>
                    ))}
                  </Select>
                  {formErrors.amount.error && (
                    <span style={{ display: "block", fontSize: 12, color: "#f87171", marginTop: 4 }}>
                      {formErrors.amount.message}
                    </span>
                  )}
                </div>

                {/* Quantity */}
                <div style={{ flex: "2 1 80px" }}>
                  <label style={{ display: "block", color: "#fff", marginBottom: 6, fontFamily: "Inter", letterSpacing: "0.8px", marginTop: 10, fontSize: 14 }}>
                    Qty
                  </label>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 12,
                    overflow: "hidden",
                    background: "rgba(255,255,255,0.035)",
                    height: 44,
                  }}>
                    <button
                      type="button"
                      onClick={handleDecrease}
                      style={{ width: 40, height: "100%", background: "transparent", border: "none", color: "#c3c8cc", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <Minus size={14} />
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue === "") { setQuantity(""); return; }
                        handleQuantityChange(Number(inputValue));
                      }}
                      onKeyDown={(e) => {
                        const inv = ["e", "E", ".", ",", "+", "-"];
                        if (inv.includes(e.key) || (!/^\d$/.test(e.key) && !["Backspace","Delete","ArrowLeft","ArrowRight","Tab","Enter","Home","End"].includes(e.key) && !e.ctrlKey && !e.metaKey)) e.preventDefault();
                      }}
                      onPaste={(e) => { if (!/^\d+$/.test(e.clipboardData.getData("text"))) e.preventDefault(); }}
                      onWheel={(e) => e.target.blur()}
                      style={{
                        flex: 1,
                        textAlign: "center",
                        background: "transparent",
                        border: "none",
                        color: "#fff",
                        fontSize: 14,
                        outline: "none",
                        padding: "0 4px",
                        width: "100%",
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleIncrease}
                      style={{ width: 40, height: "100%", background: "transparent", border: "none", color: "#c3c8cc", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  {formErrors.quantity.error && (
                    <span style={{ display: "block", fontSize: 12, color: "#f87171", marginTop: 4 }}>
                      {formErrors.quantity.message}
                    </span>
                  )}
                </div>
              </div>

              {/* Buy Now + Savings */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, justifyContent: "center", alignItems: "center", marginTop: 8 }}>
                <Button
                  style={{ width: "100%", alignSelf: "center" }}
                  disabled={formErrors.quantity.error || quantity === 0 || quantity === ""}
                  onClick={handleSendOtp}
                >
                  Buy Now for{" "}
                  <span style={{ textDecoration: "line-through", color: "#7a7a7a", marginRight: 4, marginLeft: 4 }}>
                    ${cardetails?.newTotalAmount || 0}
                  </span>
                  <span>${cardetails?.discountedAmount || 0}</span>
                </Button>
                <div style={{
                  width: "fit-content",
                  height: 28,
                  background: "linear-gradient(90deg, #00ffb224, #00ffb21a 55.12%, rgba(59,15,12,0) 90%)",
                  padding: "0 10px",
                  borderRadius: 24,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "rgba(27,178,130,1)",
                  letterSpacing: 1,
                }}>
                  <span style={{ fontWeight: 700, fontSize: "0.7em" }}>You are saving {cardetails?.boost}%</span>
                </div>
              </div>

              <p style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.7)", textAlign: "center", marginTop: 4 }}>
                It may take up to 72 hours to deliver the card details.
              </p>
            </div>
          </div>
        </div>

        {/* Accordions */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 8 }}>
          <details style={{ border: "1px solid #3C3C3C", borderRadius: 2, padding: "0 8px" }}>
            <summary style={{ padding: "12px 8px", cursor: "pointer", fontWeight: 700, fontSize: 16, backgroundColor: "#000", listStyle: "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              Description <ChevronDown size={16} />
            </summary>
            <div style={{ backgroundColor: "#000", padding: "20px 25px", textAlign: "left" }}>
              <div style={{ backgroundColor: "#FFFFFF14", padding: 16, borderRadius: 8 }}>
                <span>{cardetails?.description || "NA"}</span>
              </div>
            </div>
          </details>
          <details style={{ border: "1px solid #3C3C3C", borderRadius: 2, padding: "0 8px" }}>
            <summary style={{ padding: "12px 8px", cursor: "pointer", fontWeight: 700, fontSize: 16, backgroundColor: "#000", listStyle: "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              Terms & Conditions <ChevronDown size={16} />
            </summary>
            <div style={{ backgroundColor: "#000", padding: "20px 25px", textAlign: "left" }}>
              <div style={{ backgroundColor: "#FFFFFF14", padding: 16, borderRadius: 8 }}>
                {cardetails?.tnc || "NA"}
              </div>
            </div>
          </details>
        </div>
      </div>

      {/* ── OTP Verification Dialog ───────────────────────────── */}
      <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Gift Card Purchase</DialogTitle>
            <DialogDescription>
              For security purposes, please enter the 6-digit verification code sent to your registered email address.
            </DialogDescription>
          </DialogHeader>

          <div style={{ marginTop: 16 }}>
            <label style={{ display: "block", color: "#fff", marginBottom: 8, fontFamily: "Inter", letterSpacing: "0.8px", fontSize: 14 }}>
              Verification Code
            </label>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <OtpInput
                value={verificationCode}
                onChange={setVerificationCode}
                numInputs={6}
                renderSeparator={<span style={{ width: 4 }} />}
                inputType="number"
                renderInput={(props) => (
                  <input
                    {...props}
                    style={{
                      width: "clamp(30px, calc(100vw / 12), 70px)",
                      height: "clamp(35px, calc(100vw / 12), 60px)",
                      fontSize: "clamp(14px, calc(100vw / 40), 20px)",
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.2)",
                      backgroundColor: "transparent",
                      color: "white",
                      textAlign: "center",
                      outline: "none",
                      caretColor: "#00ADB5",
                      transition: "all 0.2s ease",
                      margin: "0 2px",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    }}
                    autoComplete="one-time-code"
                  />
                )}
                containerStyle={{ display: "flex", justifyContent: "flex-start", marginBottom: 10, maxWidth: "100%", overflow: "visible" }}
              />
            </div>
            {codeError && (
              <span style={{ display: "block", fontSize: 12, color: "#f87171", marginBottom: 10 }}>
                {codeErrorMessage || "Please enter a valid verification code"}
              </span>
            )}
            <div style={{ display: "flex", flexDirection: "row", gap: 8, alignItems: "center", marginTop: 8 }}>
              <Info size={16} />
              <span>{`We've sent an verification code to your email: ${user?.email}`}</span>
            </div>
          </div>

          <DialogFooter style={{ marginTop: 16 }}>
            <Button variant="outline" onClick={handleClose} style={{ marginRight: 8 }}>Cancel</Button>
            <Button disabled={verificationCode.length !== 6} onClick={handleVerifyCode}>Verify</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GiftCard;
